/* eslint-disable no-console */
import { ApplicationRef, Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, first, tap } from 'rxjs/operators';
import { interval, merge } from 'rxjs';
import { Router } from '@angular/router';
import { datadogRum } from '@datadog/browser-rum';
import { environment } from 'environments/environment';

interface AppData {
	forceUpdate: boolean;
}

const PATH_GENERAL_ENTRANCE = '/general-entrance';
const PATH_START = '/';

@Injectable({
	providedIn: 'root',
})
export class CheckForUpdateService {
	constructor(private readonly appRef: ApplicationRef, private readonly swUpdate: SwUpdate, private router: Router) {}

	public init(): void {
		if (environment.enableServiceWorker) {
			this.setIntervalUpdate();
			this.setPromptUpdate();
		}
	}

	private setIntervalUpdate(): void {
		const appIsStable$ = this.appRef.isStable.pipe(
			first(isStable => isStable),
			tap(() => console.log('App is stable now'))
		);
		const everySixtyMinutes$ = interval(60 * 60 * 1000);
		const everySixtyMinutesOrAppIsStable$ = merge(appIsStable$, everySixtyMinutes$);

		everySixtyMinutesOrAppIsStable$.subscribe(async () => {
			try {
				const updateFound = await this.swUpdate.checkForUpdate();
				datadogRum.addAction(updateFound ? 'NEW VERSION AVAILABLE' : 'NO NEW VERSIONS');
			} catch (err) {
				console.error('Failed to check for updates:', err);
			}
		});
	}

	private setPromptUpdate(): void {
		this.swUpdate.versionUpdates
			.pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
			.subscribe(evt => {
				datadogRum.addAction('VERSION AVAILABLE', evt);

				const forceUpdate =
					(evt.latestVersion.appData as AppData).forceUpdate ||
					this.router.routerState.snapshot.url.includes(PATH_GENERAL_ENTRANCE) ||
					this.router.routerState.snapshot.url.includes(PATH_START);

				this.updateVersionAlert(forceUpdate);
			});
	}

	private updateVersionAlert(force: boolean): void {
		if (!force) return;
		window.location.reload();
	}
}
